import * as THREE from 'three';
import { applyVertexPositions, buildGeometryFromTopology } from './mesh-topology.js';

// Matrix decomposition and pointer projection introduce float noise, same
// tolerance transform-tools.js uses for whole-object drags.
const EPSILON = 1e-6;

/**
 * Moves selected mesh vertices with the same shared `viewport.transformControls`
 * gizmo object-mode transforms use, following transform-tools.js's exact
 * shape: mouseDown snapshots the selection, objectChange previews the edit
 * locally (mutating the live topology/geometry so the drag looks correct as
 * it happens), mouseUp reverts that local preview and issues exactly one
 * `setVertexPositions` command — the same command an agent would send, with
 * the real, authoritative update arriving through the normal command
 * execution path afterward.
 *
 * Only active while mesh Edit Mode is in vertex mode with a selection —
 * `transformTools.setSuspended(true)` (main.js) hands this module exclusive
 * use of `control` for that duration, so the two never fight over it.
 */
export function createMeshEditTools({ viewport, registry, meshSelection, bridge }) {
  const control = viewport.transformControls;
  const canvas = viewport.renderer.domElement;
  const proxy = new THREE.Object3D();
  proxy.name = 'Vertex selection pivot';
  let drag = null;
  let destroyed = false;

  function activeObject() {
    const id = meshSelection.meshId;
    return id ? registry.get(id) : null;
  }

  function activeTopology(object) {
    return object?.userData?.anvilParams?.topology ?? null;
  }

  function removeProxy() {
    if (control.object === proxy) control.detach();
    proxy.removeFromParent();
  }

  function eligible() {
    return meshSelection.active && meshSelection.elementType === 'vertex' && meshSelection.ids.length > 0;
  }

  /**
   * Re-center and (re)attach the gizmo on the current vertex selection. Safe
   * to call any time, including while mesh Edit Mode is inactive — this
   * module only owns `control` while `transformTools.setSuspended(true)` is
   * in effect, so when there's nothing eligible to show, `removeProxy()`
   * releases *this module's own* proxy if it's the one attached and
   * otherwise leaves `control` alone. Calling `control.detach()`
   * unconditionally here would also detach whatever transform-tools.js just
   * attached for ordinary object-mode editing, since both modules share one
   * `viewport.transformControls` instance.
   */
  function refresh() {
    if (destroyed || drag) return;
    const object = activeObject();
    const topology = activeTopology(object);
    if (!eligible() || !object || !topology) {
      removeProxy();
      return;
    }

    const byId = new Map(topology.vertices.map((vertex) => [vertex.id, vertex]));
    object.updateWorldMatrix(true, false);
    const bounds = new THREE.Box3();
    for (const id of meshSelection.ids) {
      const vertex = byId.get(id);
      if (!vertex) continue;
      bounds.expandByPoint(new THREE.Vector3(...vertex.position).applyMatrix4(object.matrixWorld));
    }
    if (bounds.isEmpty()) {
      removeProxy();
      return;
    }

    control.enabled = true;
    control.setMode('translate');
    if (!proxy.parent) viewport.helpers.add(proxy);
    proxy.position.copy(bounds.getCenter(new THREE.Vector3()));
    // proxy.position is local to its parent (viewport.helpers), not world
    // space — convert, matching transform-tools.js's identical multi-select
    // proxy placement. viewport.helpers currently always has an identity
    // transform relative to the scene (see scene.js), so this is a no-op
    // today, but previewDrag()'s delta math below assumes proxy.position
    // deltas are directly comparable to world-space vertex positions, and
    // that stops being true the moment anything ever gives helpers its own
    // transform.
    viewport.helpers.worldToLocal(proxy.position);
    proxy.quaternion.identity();
    proxy.scale.set(1, 1, 1);
    proxy.updateMatrixWorld(true);
    control.attach(proxy);
  }

  function startDrag() {
    if (control.object !== proxy) return;
    const object = activeObject();
    const topology = activeTopology(object);
    if (!object || !topology) return;
    const byId = new Map(topology.vertices.map((vertex) => [vertex.id, vertex]));
    const snapshots = meshSelection.ids
      .map((id) => byId.get(id))
      .filter(Boolean)
      .map((vertex) => ({ id: vertex.id, position: [...vertex.position] }));
    if (snapshots.length === 0) return;

    object.updateWorldMatrix(true, false);
    drag = {
      object,
      topology,
      snapshots,
      startProxyPosition: proxy.position.clone(),
      controlsEnabled: viewport.controls.enabled,
    };
    viewport.controls.enabled = false;
  }

  /** Pure: what each dragged vertex's new *local* topology position would be for a given world-space offset. No mutation. */
  function computeChanges(state, delta) {
    const inverse = new THREE.Matrix4().copy(state.object.matrixWorld).invert();
    return state.snapshots.map(({ id, position }) => {
      const world = new THREE.Vector3(...position).applyMatrix4(state.object.matrixWorld).add(delta);
      return [id, world.applyMatrix4(inverse).toArray()];
    });
  }

  /** Apply pre-computed changes to the topology and rebuild the render geometry from it. */
  function applyChanges(state, changes) {
    applyVertexPositions(state.topology, changes);
    state.object.geometry.dispose();
    state.object.geometry = buildGeometryFromTopology(state.topology);
  }

  function previewDrag() {
    if (!drag) return;
    const delta = proxy.position.clone().sub(drag.startProxyPosition);
    applyChanges(drag, computeChanges(drag, delta));
    onPreview();
  }

  function revert(state) {
    applyChanges(state, state.snapshots.map(({ id, position }) => [id, position]));
  }

  function finishDrag(commit) {
    if (!drag) return;
    const state = drag;
    drag = null;
    viewport.controls.enabled = state.controlsEnabled;

    const delta = proxy.position.clone().sub(state.startProxyPosition);
    const changed = commit && delta.length() > EPSILON;
    // Compute what the commit *would* change without touching the topology —
    // the local preview is about to be reverted wholesale below, so there's
    // no need to apply this drag's final offset only to immediately undo it.
    const changes = changed ? computeChanges(state, delta) : null;

    // Always revert the local preview before issuing (or skipping) the real
    // command — the authoritative update, if any, arrives through the
    // normal command-execution path once the promise below resolves.
    revert(state);
    refresh();
    onPreview();

    if (!changed) return;
    const count = changes.length;
    const label = `Move ${count} vert${count === 1 ? 'ex' : 'ices'}`;
    const request = bridge.execute('setVertexPositions', { id: state.object.userData.anvilId, changes }, { label });
    void request.catch((error) => console.error('[anvil] vertex move failed', error));
  }

  function forceEndControl() {
    canvas.removeEventListener('pointermove', control._onPointerMove);
    control.dragging = false;
    control.axis = null;
  }

  function cancel() {
    if (!drag) return false;
    finishDrag(false);
    forceEndControl();
    return true;
  }

  function commitDrag() {
    finishDrag(true);
  }

  let onPreview = () => {};

  control.addEventListener('mouseDown', startDrag);
  control.addEventListener('objectChange', previewDrag);
  control.addEventListener('mouseUp', commitDrag);

  return {
    get dragging() { return Boolean(drag); },
    /** Called after every local preview mutation (during drag, and once after revert) so the display helpers can be rebuilt. */
    onPreview(callback) {
      onPreview = typeof callback === 'function' ? callback : () => {};
    },
    refresh,
    cancel,
    destroy() {
      destroyed = true;
      cancel();
      control.removeEventListener('mouseDown', startDrag);
      control.removeEventListener('objectChange', previewDrag);
      control.removeEventListener('mouseUp', commitDrag);
      removeProxy();
    },
  };
}
